import { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, BookOpen, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { api, extractApiErrorMessage } from '../../api/client';

interface HomeData {
  usuario: { nome: string; role: string };
  resumoDoDia: {
    totalFilhos?: number;
    presentes?: number;
    ausentes?: number;
    atrasados?: number;
    proximaAula?: string;
    comunicadosNaoLidos?: number;
  };
  ultimosEventos: Array<{
    id: string;
    tipo: string;
    descricao: string;
    dataHora: string;
    aluno?: { nome: string };
  }>;
}

export function MobileHome() {
  const [data, setData] = useState<HomeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const user = useAuthStore((s) => s.user);

  const load = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const res = await api.get('/mobile/home');
      setData(res.data);
    } catch (err: any) {
      const msg = extractApiErrorMessage(err, 'Não foi possível carregar o resumo inicial.');
      setErro(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
        <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: '#7c3aed' }} />
      </div>
    );
  }

  if (erro && !data) {
    return (
      <div className="m-empty m-animate" style={{ padding: '60px 20px', textAlign: 'center' }}>
        <div className="m-empty-icon" style={{ background: '#fee2e2', color: '#dc2626', margin: '0 auto 12px' }}>
          <AlertCircle size={26} />
        </div>
        <p style={{ fontWeight: 700, color: '#1e293b', marginBottom: 4, fontSize: '0.95rem' }}>
          Falha de Carregamento
        </p>
        <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 16, lineHeight: 1.4, maxWidth: 300, margin: '0 auto 16px' }}>
          {erro}
        </p>
        <button
          type="button"
          onClick={() => void load()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 16px',
            borderRadius: 10,
            border: 'none',
            background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '0.82rem',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)',
          }}
        >
          <RefreshCw size={14} />
          Tentar novamente
        </button>
      </div>
    );
  }

  const r = data?.resumoDoDia || {};
  const nome = user?.nome?.split(' ')[0] || data?.usuario?.nome?.split(' ')[0] || 'Usuário';
  const hora = new Date().getHours();
  const saudacao = hora < 12 ? 'Bom dia' : hora < 18 ? 'Boa tarde' : 'Boa noite';

  return (
    <div className="m-animate">
      {/* Saudação */}
      <div style={{ marginBottom: 20 }}>
        <p style={{ color: '#9ca3af', fontSize: '0.82rem', margin: 0 }}>{saudacao},</p>
        <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#1e293b', margin: '2px 0' }}>{nome} 👋</h2>
        <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>
          {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="m-kpi-row">
        <div className="m-kpi">
          <span className="m-kpi-value" style={{ color: '#16a34a' }}>
            {r.presentes ?? '—'}
          </span>
          <span className="m-kpi-label">Presentes</span>
        </div>
        <div className="m-kpi">
          <span className="m-kpi-value" style={{ color: '#dc2626' }}>
            {r.ausentes ?? '—'}
          </span>
          <span className="m-kpi-label">Ausentes</span>
        </div>
      </div>

      <div className="m-kpi-row">
        <div className="m-kpi">
          <span className="m-kpi-value" style={{ color: '#d97706' }}>
            {r.atrasados ?? '—'}
          </span>
          <span className="m-kpi-label">Atrasados</span>
        </div>
        <div className="m-kpi">
          <span className="m-kpi-value" style={{ color: '#7c3aed' }}>
            {r.comunicadosNaoLidos ?? 0}
          </span>
          <span className="m-kpi-label">Avisos</span>
        </div>
      </div>

      {/* Próxima Aula */}
      {r.proximaAula && (
        <div className="m-card">
          <div className="m-card-header">
            <div className="m-card-icon" style={{ background: '#ede9fe', color: '#7c3aed' }}>
              <BookOpen size={20} />
            </div>
            <div>
              <div className="m-card-title">Próxima aula</div>
              <div className="m-card-sub">{r.proximaAula}</div>
            </div>
          </div>
        </div>
      )}

      {/* Últimos Eventos */}
      <p className="m-section-title">Atividade recente</p>
      {data?.ultimosEventos?.length ? (
        <div className="m-timeline">
          {data.ultimosEventos.slice(0, 8).map((ev) => {
            const dotClass =
              ev.tipo === 'ENTRADA' ? 'm-timeline-dot-green'
              : ev.tipo === 'SAIDA' ? 'm-timeline-dot-red'
              : ev.tipo === 'ATRASO' ? 'm-timeline-dot-yellow'
              : 'm-timeline-dot-blue';
            return (
              <div key={ev.id} className="m-timeline-item">
                <div className={`m-timeline-dot ${dotClass}`} />
                <div className="m-timeline-time">
                  {new Date(ev.dataHora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  {ev.aluno && ` · ${ev.aluno.nome}`}
                </div>
                <div className="m-timeline-text">{ev.descricao}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="m-empty">
          <div className="m-empty-icon">
            <CheckCircle2 size={24} />
          </div>
          <p>Nenhum evento registrado hoje</p>
        </div>
      )}
    </div>
  );
}
